/**
 * 기상 패널 고정효과 회귀 결과 (DESIGN §8.3 H1~H5 · §8.5 P6).
 *
 * **지금은 식별되지 않는다.** 기상 열이 전부 결측이라 β 를 추정할 정보 자체가 없다.
 * 그래서 이 카드는 계수를 하나도 그리지 않고 **왜 못 그리는지**만 말한다 —
 * "효과가 없다"와 "물어볼 수 없다"는 전혀 다른 진술이고, 빈 표를 그리면 앞쪽으로
 * 읽힌다 (CLAUDE.md 절대원칙 2).
 *
 * **유의성은 p 가 아니라 q 로 판정한다.** 가설이 다섯이고 래그가 넷이라 검정이 수십
 * 개가 되는데, p<0.05 만 보면 가족단위 오탐률이 실측 0.50 이었다 (BH 보정 후 0.08).
 * 그리고 `q_value = null` 은 **"유의하지 않음"이 아니라 "검정 가족에 없음"** 이다 —
 * 배지를 세 갈래로 나누는 이유가 이것이다. 두 갈래로 만들면 가족 밖 계수가 조용히
 * "유의하지 않은 계수"로 합쳐진다.
 *
 * **눈금 설명(`encoding`)을 미식별 상태에서는 주지 않는다.** 그릴 축이 없는데
 * "가로축은 …" 이라고 적으면 그 줄 자체가 거짓이 된다. 결과가 생겨 계수 표를 그리는
 * 순간에는 표의 열이 곧 눈금이므로 그때 채운다 (`OperatorMatrix` 와 같은 방식).
 */

import { useQuery } from '@tanstack/react-query'

import { ChartFrame } from '@/components/ChartFrame'
import { ApiRequestError, fetchEnvelope } from '@/lib/api'
import { DASH } from '@/lib/fmt'
import type { Envelope } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

/** BH 보정 후 기준. p 가 아니라 q 에 건다 (§7.2). */
const Q_ALPHA = 0.05

interface Coefficient {
  term: string
  label?: string | null
  hypothesis?: string | null
  lag_days?: number | null
  estimate: number | null
  std_error?: number | null
  ci?: [number, number] | null
  /** 다중비교 **보정 전** 값. 배지 판정에 쓰지 않는다 */
  p_value?: number | null
  /** BH 보정 후. `null` 은 "유의하지 않음"이 아니라 "검정 가족에 없음" */
  q_value?: number | null
}

interface PanelData {
  identified: boolean
  coefficients?: Coefficient[]
  reason?: string | null
  n_obs?: number | null
  n_chargers?: number | null
}

type Verdict = 'significant' | 'not-significant' | 'outside'

interface Badge {
  verdict: Verdict
  label: string
  title: string
}

/**
 * 유의성 배지 — **세 갈래다.** `q_value` 가 없는 계수를 "유의하지 않음"으로 접으면
 * 검정 가족에 넣지 않기로 한 결정이 결과처럼 보인다.
 */
function badgeOf(c: Coefficient): Badge {
  if (c.q_value == null) {
    return {
      verdict: 'outside',
      label: '검정 가족 밖',
      title:
        'q_value 가 없습니다. 다중비교 보정 가족에 포함되지 않았다는 뜻이며 "유의하지 않음"과 다릅니다.',
    }
  }
  if (c.q_value < Q_ALPHA) {
    return {
      verdict: 'significant',
      label: `유의 · q=${c.q_value.toFixed(3)}`,
      title: `BH 보정 후 q < ${Q_ALPHA} 입니다. p 는 보정 전 값이라 판정에 쓰지 않았습니다.`,
    }
  }
  return {
    verdict: 'not-significant',
    label: `유의하지 않음 · q=${c.q_value.toFixed(3)}`,
    title: `BH 보정 후 q ≥ ${Q_ALPHA} 입니다.`,
  }
}

const num = (v: number | null | undefined, digits = 4): string =>
  v == null || Number.isNaN(v) ? DASH : v.toFixed(digits)

export function PanelResult() {
  const filters = useFilters()

  const q = useQuery({
    queryKey: ['reconcile/panel_fe', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<PanelData>('/reconcile/panel_fe', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
    // 결과가 아직 없는 상태가 정상 경로다. 재시도로 시간을 쓰지 않는다.
    retry: false,
  })

  const err = q.error as Error | null
  // 404 는 "서버가 아프다"가 아니라 "이 빌드에 아직 그 결과가 없다"이다.
  // 일반 오류 카드로 밀면 사유가 "요청이 실패했습니다"로 뭉개진다.
  const endpointMissing = err instanceof ApiRequestError && err.status === 404
  const hardError = endpointMissing ? null : err

  const env = q.data as Envelope<PanelData> | undefined
  const data = env?.data
  const identified = data?.identified === true
  const coefficients = data?.coefficients ?? []

  const reason = endpointMissing
    ? '이 빌드의 API 에 /reconcile/panel_fe 가 없습니다 — 패널 회귀 결과가 아직 만들어지지 않았습니다.'
    : (data?.reason ?? null)

  const table = (
    // 계수가 많아지면 `.ctable`(340px)이 잘려 스크롤된다. 스크롤되는 영역은 포커스를
    // 받아야 키보드로 아래쪽 행에 닿는다 (axe `scrollable-region-focusable`)
    <div className="ctable" tabIndex={0} role="region" aria-label="기상 패널 고정효과 회귀 계수 표">
      <table className="data-table">
        <caption className="sr-only">
          기상 패널 고정효과 회귀 계수 — 유의성은 BH 보정 후 q 로 판정합니다
        </caption>
        <thead>
          <tr>
            <th scope="col">가설</th>
            <th scope="col">항</th>
            <th scope="col" className="ta-r">
              래그(일)
            </th>
            <th scope="col" className="ta-r">
              추정치 β
            </th>
            <th scope="col" className="ta-r">
              표준오차
            </th>
            <th scope="col" className="ta-r">
              95% 구간
            </th>
            <th scope="col" className="ta-r">
              p (보정 전)
            </th>
            <th scope="col" className="ta-r">
              q (BH 보정)
            </th>
            <th scope="col">판정</th>
          </tr>
        </thead>
        <tbody>
          {coefficients.map((c) => {
            const badge = badgeOf(c)
            return (
              <tr key={`${c.hypothesis ?? ''}|${c.term}|${c.lag_days ?? ''}`}>
                <td>{c.hypothesis ?? DASH}</td>
                <td className="mono">{c.label ?? c.term}</td>
                <td className="num ta-r">{c.lag_days ?? DASH}</td>
                <td className="num ta-r">{num(c.estimate)}</td>
                <td className="num ta-r">{num(c.std_error)}</td>
                <td className="num ta-r">
                  {c.ci ? `${num(c.ci[0])} ~ ${num(c.ci[1])}` : DASH}
                </td>
                <td className="num ta-r preg__p">{num(c.p_value, 3)}</td>
                <td className="num ta-r">{c.q_value == null ? DASH : num(c.q_value, 3)}</td>
                <td>
                  <span className={`preg__badge preg__badge--${badge.verdict}`} title={badge.title}>
                    {badge.label}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="기상 패널 고정효과 회귀"
      question="날씨가 고장을 설명하는가 — 지금은 답할 수 없고, 못 하는 이유가 결과다"
      meta={env?.meta}
      /* 이 엔드포인트의 `meta.coverage` 는 자리표시자다 — 결과가 없으면 0.0, 있으면
       * 1.0 을 그냥 써 보낸다. 관측 커버리지가 아니므로 마이크로바를 걸면 "이 카드는
       * 아무것도 관측하지 못했다"(0.0%)는 데이터 사실을 주장하게 된다. 실제 진술은
       * "적합 결과 파일이 없다"이고 그건 아래 상자가 말한다. 이 카드는 가동률을
       * 인쇄하지 않으므로 바를 내려도 §7.1 계약을 깨지 않는다. */
      hideCoverage
      reading={
        identified
          ? '충전기 고정효과 패널의 계수입니다. 한 행이 검정 하나이며, 유의성은 다중비교 보정 후 q 로만 판정합니다.'
          : '이 카드는 계수를 보여주지 않습니다. 모형이 식별되지 않은 상태이며, 그 사유를 그대로 적었습니다 — 추정할 수 없는 값을 빈칸이나 0 으로 채우지 않습니다.'
      }
      encoding={
        identified
          ? [
              {
                role: '추정치 β',
                what: '기상 변수 1단위가 고장률을 움직이는 크기',
                detail:
                  '충전기 고정효과를 넣었으므로 충전기 간 차이가 아니라 같은 충전기 안에서의 시간 변화입니다.',
              },
              {
                role: 'p (보정 전)',
                what: '단일 검정 기준 유의확률',
                detail:
                  '표시는 하되 판정에 쓰지 않습니다. 가설 5 × 래그 4 라 검정이 수십 개이고, p<0.05 만 보면 가족단위 오탐률이 실측 0.50 이었습니다.',
              },
              {
                role: 'q (BH 보정)',
                what: '판정에 실제로 쓰는 값',
                detail: `q < ${Q_ALPHA} 일 때만 "유의"로 적습니다. BH 보정 후 오탐률은 실측 0.08 이었습니다.`,
              },
              {
                role: '판정 배지',
                what: '유의 · 유의하지 않음 · 검정 가족 밖 (3갈래)',
                detail:
                  'q 가 없는 계수를 "유의하지 않음"으로 접으면 검정 가족에 넣지 않기로 한 결정이 결과처럼 보입니다. 그래서 갈래를 하나 더 둡니다.',
              },
            ]
          : undefined
      }
      isLoading={q.isLoading}
      error={hardError}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '유의성 배지는 q 로만 판정한다.',
          text: `p 는 다중비교 보정 전이라 표에만 남기고 배지에는 쓰지 않습니다. 가설 5개 × 래그 4개면 검정이 수십 개가 되어 p<${Q_ALPHA} 만으로는 우연을 걸러내지 못합니다.`,
        },
        {
          label: 'q_value 가 null 인 것은 "유의하지 않음"이 아니다.',
          text: '그 계수를 다중비교 보정 가족에 넣지 않았다는 뜻입니다. 두 경우를 같은 배지로 묶으면 분석자의 결정이 데이터의 결론으로 둔갑합니다.',
        },
        {
          label: '식별되지 않은 모형의 계수를 지어내지 않는다.',
          text: '기상 열이 전부 결측이면 β 를 추정할 정보가 없습니다. 0 이나 빈칸으로 채우면 "효과가 없다"로 읽히는데, 실제 진술은 "물어볼 수 없다"입니다.',
        },
        {
          label: '관측소 거리 상위 5% 는 제외 옵션이다.',
          text: '먼 관측소의 기상값을 그 충전기의 날씨로 쓰면 측정오차가 β 를 0 쪽으로 끌어당깁니다 (§8.3).',
        },
        {
          label: '이 카드는 상단 필터를 타지 않는다.',
          text: '적합은 CLI 가 전국 패널로 한 번 돌리고 API 는 그 결과 파일을 읽기만 합니다. 사업자·지역을 걸어도 계수가 그 부분모집단의 값으로 바뀌지 않으니, 결과가 생긴 뒤에도 필터를 건 채로 읽지 마세요.',
        },
      ]}
    >
      {identified && coefficients.length > 0 ? (
        table
      ) : (
        <div className="preg__unident verdict-note" role="status" data-testid="panel-unidentified">
          <p>
            <strong>모형이 식별되지 않았습니다.</strong> 계수를 하나도 표시하지 않습니다 —
            추정할 수 없는 값을 0 이나 빈칸으로 채우면 "효과가 없다"로 읽힙니다.
          </p>

          <p className="preg__reason">
            <span className="preg__reasonlabel">API 가 밝힌 사유</span>
            <span className="preg__reasontext">
              {reason ?? '응답이 사유를 밝히지 않았습니다 (identified=false 만 왔습니다)'}
            </span>
          </p>

          {(env?.meta.notes ?? []).length > 0 && (
            <ul className="preg__apinotes">
              {(env?.meta.notes ?? []).map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}

          <p className="preg__needlabel">이 결과가 생기려면 (DESIGN §8.3 · §7.3)</p>
          <ol className="preg__steps">
            <li>
              <code>data/ref/asos_stations.csv</code> — 관측소 좌표 기준 파일
            </li>
            <li>
              <code>evca collect weather --backfill</code> — 기상 자료 적재
            </li>
            <li>
              <code>evca analysis panel</code> — 적합. 설계행렬이 GB 단위라 API 가 요청 시 돌리지
              않습니다
            </li>
          </ol>

          <p className="verdict-note__hint">
            "효과가 없다"가 아니라 <em>"아직 물어볼 수 없다"</em>입니다. 기상 열이 전부 결측이면
            β 를 추정할 정보 자체가 없습니다.
          </p>
        </div>
      )}
    </ChartFrame>
  )
}
